import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, UserPlus, User, Users, Search, Trash2, Check, X, Clock, Bus, ChevronDown, ChevronUp, Download, MessageCircle, Bell, Pencil, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import api from '../lib/api';

export default function GuestsPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [families, setFamilies] = useState([]);
  const [guests, setGuests] = useState([]);
  const [publicRSVPs, setPublicRSVPs] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [showAddFamilyDialog, setShowAddFamilyDialog] = useState(false);
  const [showAddGuestDialog, setShowAddGuestDialog] = useState(false);
  const [showNotificationsDialog, setShowNotificationsDialog] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [newFamily, setNewFamily] = useState({ name: '', phone: '' });
  const [newGuest, setNewGuest] = useState({ first_name: '', last_name: '', phone: '' });
  const [showEditFamilyDialog, setShowEditFamilyDialog] = useState(false);
  const [showEditGuestDialog, setShowEditGuestDialog] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [editingGuest, setEditingGuest] = useState(null);
  const [familySearchQuery, setFamilySearchQuery] = useState('');

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/events/' + eventId + '/notifications');
      setNotifications(res.data);
    } catch (err) { }
  }, [eventId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventRes, familiesRes, guestsRes, publicRes, statsRes] = await Promise.all([
          api.get('/events/' + eventId),
          api.get('/events/' + eventId + '/families'),
          api.get('/events/' + eventId + '/guests'),
          api.get('/events/' + eventId + '/public-rsvps'),
          api.get('/events/' + eventId + '/stats')
        ]);
        setEvent(eventRes.data);
        setFamilies(familiesRes.data);
        setGuests(guestsRes.data);
        setPublicRSVPs(publicRes.data || []);
        setStats(statsRes.data);
        fetchNotifications();
      } catch (err) {
        toast.error('Error');
        navigate('/dashboard');
      }
      setLoading(false);
    };
    fetchData();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [eventId, navigate, fetchNotifications]);

  const handleCreateFamily = async () => {
    if (!newFamily.name) { toast.error('Nombre requerido'); return; }
    try {
      const res = await api.post('/events/' + eventId + '/families', newFamily);
      setFamilies([...families, res.data]);
      setShowAddFamilyDialog(false);
      setNewFamily({ name: '', phone: '' });
      toast.success('Familia añadida');
    } catch (err) { toast.error('Error'); }
  };

  const handleCreateGuest = async () => {
    if (!newGuest.first_name) { toast.error('Nombre requerido'); return; }

    let targetFamilyId = selectedFamily;

    try {
      // Si no se selecciona familia, creamos una automática para este invitado individual
      if (!targetFamilyId) {
        const familyRes = await api.post('/events/' + eventId + '/families', {
          name: `${newGuest.first_name} ${newGuest.last_name || ''}`.trim(),
          phone: newGuest.phone || ''
        });
        setFamilies([...families, familyRes.data]);
        targetFamilyId = familyRes.data.id;
      }

      const res = await api.post('/events/' + eventId + '/families/' + targetFamilyId + '/guests', { ...newGuest, family_id: targetFamilyId });
      setGuests([...guests, res.data]);
      setShowAddGuestDialog(false);
      setNewGuest({ first_name: '', last_name: '', phone: '' });
      setSelectedFamily(null);
      toast.success('Invitado añadido');
    } catch (err) { toast.error('Error al añadir invitado'); }
  };

  const handleDeleteFamily = async (familyId) => {
    if (!window.confirm('¿Eliminar esta familia?')) return;
    try {
      await api.delete('/events/' + eventId + '/families/' + familyId);
      setFamilies(families.filter(f => f.id !== familyId));
      setGuests(guests.filter(g => g.family_id !== familyId));
      toast.success('Eliminada');
    } catch (err) { toast.error('Error'); }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!window.confirm('¿Eliminar este invitado?')) return;
    try {
      await api.delete('/events/' + eventId + '/guests/' + guestId);
      setGuests(guests.filter(g => g.id !== guestId));
      toast.success('Eliminado');
    } catch (err) { toast.error('Error'); }
  };
  const handleUpdateFamily = async () => {
    if (!editingFamily.name) { toast.error('Nombre requerido'); return; }
    try {
      const res = await api.put('/events/' + eventId + '/families/' + editingFamily.id, editingFamily);
      setFamilies(families.map(f => f.id === editingFamily.id ? res.data : f));
      setShowEditFamilyDialog(false);
      setEditingFamily(null);
      toast.success('Familia actualizada');
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handleUpdateGuest = async () => {
    if (!editingGuest.first_name) { toast.error('Nombre requerido'); return; }

    const currentGuest = guests.find(g => g.id === editingGuest.id);
    const oldFamilyId = currentGuest?.family_id;
    const newFamilyId = editingGuest.family_id;

    try {
      const res = await api.put('/events/' + eventId + '/guests/' + editingGuest.id, editingGuest);
      const newGuests = guests.map(g => g.id === editingGuest.id ? res.data : g);
      setGuests(newGuests);

      // Si la familia ha cambiado, comprobar si la anterior se ha quedado vacía
      if (oldFamilyId && oldFamilyId !== newFamilyId) {
        const remainingInOld = newGuests.filter(g => g.family_id === oldFamilyId);
        if (remainingInOld.length === 0) {
          // La familia anterior está vacía, la eliminamos automáticamente para limpiar la interfaz
          await api.delete('/events/' + eventId + '/families/' + oldFamilyId);
          setFamilies(prev => prev.filter(f => f.id !== oldFamilyId));
          toast.success('Invitado movido y grupo antiguo eliminado por estar vacío');
        } else {
          toast.success('Invitado movido correctamente');
        }
      } else {
        toast.success('Invitado actualizado');
      }

      setShowEditGuestDialog(false);
      setEditingGuest(null);
    } catch (err) { toast.error('Error al actualizar'); }
  };

  const handlePurgeGuests = async () => {
    if (!window.confirm('¿ESTÁS SEGURO? Esto eliminará TODOS los invitados y familias de este evento. Esta acción no se puede deshacer.')) return;
    try {
      await api.delete('/events/' + eventId + '/purge-guests');
      setFamilies([]);
      setGuests([]);
      setStats({
        total_guests: 0,
        confirmed: 0,
        pending: 0,
        declined: 0,
        total_families: 0,
        families_confirmed: 0,
        families_need_transport: 0,
        public_confirmed: 0,
        public_total: 0,
        grand_total_confirmed: 0
      });
      toast.success('Lista vaciada correctamente');
    } catch (err) { toast.error('Error al vaciar la lista'); }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/events/' + eventId + '/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.imported + ' invitados importados');
      window.location.reload();
    } catch (err) { toast.error('Error al importar'); }
    e.target.value = '';
  };

  const handleSendWhatsApp = async (familyId, phone = null) => {
    try {
      const url = phone
        ? `/events/${eventId}/families/${familyId}/whatsapp-link?phone=${encodeURIComponent(phone)}`
        : `/events/${eventId}/families/${familyId}/whatsapp-link`;
      const res = await api.get(url);
      window.open(res.data.whatsapp_link, '_blank');
    } catch (err) { toast.error('Error al generar enlace'); }
  };

  const handleCopyLink = (family) => {
    try {
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/invite/${event.public_token}/${family.token}`;
      navigator.clipboard.writeText(link);
      toast.success('Enlace copiado al portapapeles');
    } catch (err) {
      toast.error('Error al copiar el enlace');
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await api.get('/events/' + eventId + '/export-csv', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `invitados_${event?.title || 'boda'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('CSV exportado correctamente (datos en tiempo real)');
    } catch (err) {
      toast.error('Error al exportar CSV');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'confirmed') return <Badge className="bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />Confirmado</Badge>;
    if (status === 'declined') return <Badge className="bg-red-100 text-red-700"><X className="w-3 h-3 mr-1" />No asiste</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
  };

  const getFamilyGuests = (familyId) => guests.filter(g => g.family_id === familyId);

  const filteredFamilies = families.filter(family => {
    const familyGuests = getFamilyGuests(family.id);
    const matchesSearch = family.name.toLowerCase().includes(searchQuery.toLowerCase()) || familyGuests.some(g => (g.first_name + ' ' + g.last_name).toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || family.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const toggleFamilyExpand = (familyId) => setExpandedFamilies(prev => ({ ...prev, [familyId]: !prev[familyId] }));

  const filteredPublicRSVPs = publicRSVPs.filter(rsvp => {
    const matchesSearch = rsvp.full_name.toLowerCase().includes(searchQuery.toLowerCase());
    const status = rsvp.attending ? 'confirmed' : 'declined';
    const matchesStatus = filterStatus === 'all' || status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const downloadCSVTemplate = () => {
    const template = 'nombre,apellido,telefono,familia\nJuan,García,+34600123456,Familia García\nMaría,García,+34600123457,Familia García';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'invitados_plantilla.csv';
    a.click();
  };

  if (loading) return <div className="min-h-screen bg-wedding-background flex items-center justify-center"><div className="animate-pulse text-wedding-primary font-heading text-2xl">Cargando...</div></div>;

  return (
    <div className="min-h-screen bg-wedding-background">
      <header className="bg-white/80 backdrop-blur-md border-b border-wedding-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard"><Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button></Link>
            <div><h1 className="font-heading text-xl text-wedding-text">{event?.title}</h1><p className="text-wedding-text-muted text-sm">Gestión de Invitados</p></div>
          </div>
          <Button variant="outline" onClick={() => setShowNotificationsDialog(true)} className="relative" data-testid="notifications-btn">
            <Bell className="w-4 h-4" />
            {notifications.filter(n => !n.read).length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>}
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            <Card className="bg-slate-50/50 border-slate-100 shadow-sm transition-all hover:shadow-md hover:bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-slate-800">{(stats.total_guests || 0) + (stats.public_total || 0)}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-slate-500 mt-2">Total</p>
              </CardContent>
            </Card>

            <Card className="bg-green-50/50 border-green-100 shadow-sm transition-all hover:shadow-md hover:bg-green-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-green-600">{stats.grand_total_confirmed || 0}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-green-700 mt-2">Confirmados</p>
              </CardContent>
            </Card>

            <Card className="bg-yellow-50/50 border-yellow-100 shadow-sm transition-all hover:shadow-md hover:bg-yellow-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-yellow-600">{stats.pending || 0}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-yellow-700 mt-2">Pendientes</p>
              </CardContent>
            </Card>

            <Card className="bg-blue-50/50 border-blue-100 shadow-sm transition-all hover:shadow-md hover:bg-blue-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-blue-600">{stats.total_families || 0}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-blue-700 mt-2 leading-none">Grupos / Familias<br /><span className="text-[9px] opacity-70">Totales</span></p>
              </CardContent>
            </Card>

            <Card className="bg-purple-50/50 border-purple-100 shadow-sm transition-all hover:shadow-md hover:bg-purple-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-purple-600">{stats.families_confirmed || 0}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-purple-700 mt-2 leading-none">Grupos / Familias<br /><span className="text-[9px] opacity-70">Confirmados</span></p>
              </CardContent>
            </Card>

            <Card className="bg-indigo-50/50 border-indigo-100 shadow-sm transition-all hover:shadow-md hover:bg-indigo-50">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-heading text-indigo-600">{stats.public_confirmed || 0}</p>
                <p className="text-[10px] sm:text-[11px] uppercase tracking-wider font-bold text-indigo-700 mt-2 leading-none">Individuales<br /><span className="text-[9px] opacity-70">Confirmados</span></p>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-wedding-text-muted" /><Input placeholder="Buscar..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-guests-input" /></div>
          <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="confirmed">Confirmados</SelectItem><SelectItem value="pending">Pendientes</SelectItem><SelectItem value="declined">No asisten</SelectItem></SelectContent></Select>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-2" />Importar CSV</Button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn" className="border-green-200 text-green-700 hover:bg-green-50"><Download className="w-4 h-4 mr-2" />Exportar CSV</Button>
          <Button variant="outline" onClick={downloadCSVTemplate} data-testid="download-template-btn"><Download className="w-4 h-4 mr-2" />Plantilla CSV</Button>
          <Button variant="outline" onClick={() => setShowAddFamilyDialog(true)} data-testid="add-family-btn"><Users className="w-4 h-4 mr-2" />Nueva Familia</Button>
          <Button onClick={() => setShowAddGuestDialog(true)} className="bg-wedding-primary hover:bg-wedding-primary-dark text-white" data-testid="add-guest-btn"><UserPlus className="w-4 h-4 mr-2" />Nuevo Invitado</Button>
          <Button variant="ghost" onClick={handlePurgeGuests} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid="purge-guests-btn"><Trash2 className="w-4 h-4 mr-2" />Limpiar lista</Button>
        </div>

        <div className="space-y-4">
          {filteredFamilies.length === 0 ? (
            <Card><CardContent className="p-12 text-center"><Users className="w-12 h-12 text-wedding-text-muted mx-auto mb-4" /><h3 className="font-heading text-xl text-wedding-text mb-2">No hay invitados</h3></CardContent></Card>
          ) : filteredFamilies.map((family) => {
            const familyGuests = getFamilyGuests(family.id);
            const isExpanded = expandedFamilies[family.id];
            return (
              <Collapsible key={family.id} open={isExpanded} onOpenChange={() => toggleFamilyExpand(family.id)}>
                <Card data-testid={'family-card-' + family.id}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-wedding-accent/10 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-wedding-primary/10 rounded-full flex items-center justify-center">
                            {familyGuests.length > 1 ? (
                              <Users className="w-6 h-6 text-wedding-primary" />
                            ) : (
                              <User className="w-6 h-6 text-wedding-primary" />
                            )}
                          </div>
                          <div><CardTitle className="font-heading text-lg flex items-center gap-2">{family.name}{family.needs_transport && <Bus className="w-4 h-4 text-blue-500" />}</CardTitle><p className="text-sm text-wedding-text-muted">{familyGuests.length === 1 ? 'Invitado individual' : `${familyGuests.length} invitados`}</p></div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(family.status)}
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setSelectedFamily(family.id); setShowAddGuestDialog(true); }} className="text-wedding-primary" title="Añadir invitado"><UserPlus className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setEditingFamily(family); setShowEditFamilyDialog(true); }} className="text-blue-500" title="Editar familia"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleSendWhatsApp(family.id); }} className="text-green-600" data-testid={'send-whatsapp-' + family.id} title="Enviar WhatsApp"><MessageCircle className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleCopyLink(family); }} className="text-gray-600" title="Copiar enlace"><Copy className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleDeleteFamily(family.id); }} className="text-red-500" title="Eliminar familia"><Trash2 className="w-4 h-4" /></Button>
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="border-t border-wedding-border pt-4 space-y-3">
                        {familyGuests.map((guest) => (
                          <div key={guest.id} className="flex items-center justify-between py-2 px-4 bg-wedding-accent/10 rounded-lg" data-testid={'guest-row-' + guest.id}>
                            <div><p className="font-medium text-wedding-text">{guest.first_name} {guest.last_name}</p>{guest.phone && <p className="text-sm text-wedding-text-muted">{guest.phone}</p>}</div>
                            <div className="flex items-center gap-3">
                              {getStatusBadge(guest.status)}
                              <Button variant="ghost" size="icon" onClick={() => handleSendWhatsApp(family.id, guest.phone)} className="text-green-600 h-8 w-8" title="Enviar WhatsApp individual"><MessageCircle className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => handleCopyLink(family)} className="text-gray-600 h-8 w-8" title="Copiar enlace"><Copy className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => { setEditingGuest(guest); setShowEditGuestDialog(true); }} className="text-blue-500 h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteGuest(guest.id)} className="text-red-500 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}

          {filteredPublicRSVPs.length > 0 && (
            <div className="mt-12 space-y-4">
              <h2 className="font-heading text-xl text-wedding-text px-2">Invitados por Link Público (Individuales)</h2>
              {filteredPublicRSVPs.map((rsvp) => (
                <Card key={rsvp.id} className="border-l-4 border-l-indigo-400">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium">{rsvp.full_name}</p>
                        <p className="text-xs text-wedding-text-muted">{rsvp.email} • {rsvp.num_guests} personas</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {rsvp.attending ? (
                        <Badge className="bg-green-100 text-green-700 font-normal"><Check className="w-3 h-3 mr-1" />Asistirá</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 font-normal"><X className="w-3 h-3 mr-1" />No asistirá</Badge>
                      )}
                      {rsvp.message && (
                        <Button variant="ghost" size="icon" onClick={() => toast(rsvp.message, { title: 'Mensaje de ' + rsvp.full_name })}><MessageCircle className="w-4 h-4 text-blue-500" /></Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Dialog open={showAddFamilyDialog} onOpenChange={setShowAddFamilyDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">Nueva Familia</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Nombre *</Label><Input value={newFamily.name} onChange={e => setNewFamily({ ...newFamily, name: e.target.value })} data-testid="new-family-name" /></div>
            <div className="space-y-2"><Label>Teléfono</Label><Input value={newFamily.phone} onChange={e => setNewFamily({ ...newFamily, phone: e.target.value })} data-testid="new-family-phone" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddFamilyDialog(false)}>Cancelar</Button><Button onClick={handleCreateFamily} className="bg-wedding-primary hover:bg-wedding-primary-dark">Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddGuestDialog} onOpenChange={setShowAddGuestDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">Nuevo Invitado</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Familia (Grupo) - <span className="text-wedding-primary font-normal">Opcional para individuales</span></Label>
              <div className="space-y-2">
                <Input
                  placeholder="Buscar familia..."
                  value={familySearchQuery}
                  onChange={e => setFamilySearchQuery(e.target.value)}
                  className="h-8 text-sm"
                />
                <Select value={selectedFamily || ''} onValueChange={setSelectedFamily}>
                  <SelectTrigger data-testid="select-family">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {families
                      .filter(f => f.name.toLowerCase().includes(familySearchQuery.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(f => {
                        const count = getFamilyGuests(f.id).length;
                        return (
                          <SelectItem key={f.id} value={f.id}>
                            <div className="flex items-center justify-between w-full gap-4">
                              <span>{f.name}</span>
                              <span className="text-[10px] opacity-50 px-1.5 py-0.5 bg-wedding-accent/20 rounded uppercase">
                                {count > 1 ? `${count} pers.` : 'Indiv.'}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label>Nombre *</Label><Input value={newGuest.first_name} onChange={e => setNewGuest({ ...newGuest, first_name: e.target.value })} data-testid="new-guest-firstname" /></div><div className="space-y-2"><Label>Apellido</Label><Input value={newGuest.last_name} onChange={e => setNewGuest({ ...newGuest, last_name: e.target.value })} data-testid="new-guest-lastname" /></div></div>
            <div className="space-y-2"><Label>Teléfono</Label><Input value={newGuest.phone} onChange={e => setNewGuest({ ...newGuest, phone: e.target.value })} data-testid="new-guest-phone" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddGuestDialog(false)}>Cancelar</Button><Button onClick={handleCreateGuest} className="bg-wedding-primary hover:bg-wedding-primary-dark">Añadir</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNotificationsDialog} onOpenChange={setShowNotificationsDialog}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle className="font-heading">Notificaciones</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3 py-4">
            {notifications.length === 0 ? <p className="text-center text-wedding-text-muted py-8">No hay notificaciones</p> : notifications.map(n => (
              <div key={n.id} className={'p-4 rounded-lg border ' + (n.read ? 'bg-white' : 'bg-wedding-accent/20 border-wedding-primary/20')}>
                <p className="font-medium text-wedding-text">{n.family_name}</p>
                <p className="text-sm text-wedding-text-muted">{n.message}</p>
                <p className="text-xs text-wedding-text-muted mt-2">{new Date(n.created_at).toLocaleString('es-ES')}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditFamilyDialog} onOpenChange={setShowEditFamilyDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">Editar Familia</DialogTitle></DialogHeader>
          {editingFamily && (
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Nombre de la Familia</Label><Input value={editingFamily.name} onChange={e => setEditingFamily({ ...editingFamily, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Teléfono de contacto</Label><Input value={editingFamily.phone} onChange={e => setEditingFamily({ ...editingFamily, phone: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Estado General</Label>
                <Select value={editingFamily.status} onValueChange={v => setEditingFamily({ ...editingFamily, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pending">Pendiente</SelectItem><SelectItem value="confirmed">Confirmado</SelectItem><SelectItem value="declined">No asiste</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowEditFamilyDialog(false)}>Cancelar</Button><Button onClick={handleUpdateFamily} className="bg-wedding-primary hover:bg-wedding-primary-dark">Guardar Cambios</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditGuestDialog} onOpenChange={setShowEditGuestDialog}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading">Editar Invitado</DialogTitle></DialogHeader>
          {editingGuest && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nombre</Label><Input value={editingGuest.first_name} onChange={e => setEditingGuest({ ...editingGuest, first_name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Apellido</Label><Input value={editingGuest.last_name || ''} onChange={e => setEditingGuest({ ...editingGuest, last_name: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Teléfono</Label><Input value={editingGuest.phone || ''} onChange={e => setEditingGuest({ ...editingGuest, phone: e.target.value })} /></div>
              <div className="space-y-2">
                <Label>Mover a Familia (Grupo)</Label>
                <div className="space-y-2">
                  <Input
                    placeholder="Filtrar familias..."
                    value={familySearchQuery}
                    onChange={e => setFamilySearchQuery(e.target.value)}
                    className="h-8 text-sm"
                  />
                  <Select value={editingGuest.family_id} onValueChange={v => setEditingGuest({ ...editingGuest, family_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar familia" /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {families
                        .filter(f => f.name.toLowerCase().includes(familySearchQuery.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(f => {
                          const count = getFamilyGuests(f.id).length;
                          return (
                            <SelectItem key={f.id} value={f.id}>
                              <div className="flex items-center justify-between w-full gap-2">
                                <span>{f.name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase ${count > 1 ? 'bg-wedding-primary/10 text-wedding-primary' : 'bg-gray-100 text-gray-500'}`}>
                                  {count > 1 ? `${count} pers.` : 'Indiv.'}
                                </span>
                              </div>
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={editingGuest.status} onValueChange={v => setEditingGuest({ ...editingGuest, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="pending">Pendiente</SelectItem><SelectItem value="confirmed">Confirmado</SelectItem><SelectItem value="declined">No asiste</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowEditGuestDialog(false)}>Cancelar</Button><Button onClick={handleUpdateGuest} className="bg-wedding-primary hover:bg-wedding-primary-dark">Guardar Cambios</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
